import { createSlice, createAsyncThunk, createAction, PayloadAction } from '@reduxjs/toolkit'
import { branchesAPI, BranchOption } from 'api/branches'

export interface BranchState {
 selectedBranchId: string | null
 selectedBranch: BranchOption | null
 branches: BranchOption[]
 viewMode: 'global' | 'branch'
 loading: boolean
 canSeeGlobalView: boolean
}

function readInitialViewMode(): 'global' | 'branch' {
 const stored = localStorage.getItem('branch_view_mode')
 if (stored === 'global' || stored === 'branch') {
 return stored
 }

 return localStorage.getItem('selected_branch_id') ? 'branch' : 'global'
}

const initialState: BranchState = {
 selectedBranchId: localStorage.getItem('selected_branch_id'),
 selectedBranch: null,
 branches: [],
 viewMode: readInitialViewMode(),
 loading: false,
 canSeeGlobalView: false,
}

export const branchChanged = createAction('branch/changed')

export const fetchBranches = createAsyncThunk(
 'branch/fetchBranches',
 async (_, { rejectWithValue }) => {
 try {
 const res = await branchesAPI.listAccessible()
 return res
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || 'Failed to fetch branches')
 }
 }
)

const branchSlice = createSlice({
 name: 'branch',
 initialState,
 reducers: {
 selectBranch: (state, action: PayloadAction<string | null>) => {
 state.selectedBranchId = action.payload
 state.viewMode = action.payload ? 'branch' : 'global'
 localStorage.setItem('branch_view_mode', state.viewMode)
 if (action.payload) {
 localStorage.setItem('selected_branch_id', action.payload)
 state.selectedBranch = state.branches.find(b => b.id === action.payload) ?? null
 } else {
 localStorage.removeItem('selected_branch_id')
 state.selectedBranch = null
 }
 window.dispatchEvent(new CustomEvent('branch:changed'))
 },
 setCanSeeGlobalView: (state, action: PayloadAction<boolean>) => {
 state.canSeeGlobalView = action.payload
 },
 clearBranch: (state) => {
 state.selectedBranchId = null
 state.selectedBranch = null
 state.branches = []
 state.viewMode = 'global'
 state.canSeeGlobalView = false
 localStorage.removeItem('selected_branch_id')
 localStorage.setItem('branch_view_mode', 'global')
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchBranches.pending, (state) => {
 state.loading = true
 })
 .addCase(fetchBranches.fulfilled, (state, action: any) => {
 state.loading = false
 const branches = action.payload?.data?.branches ?? []
 state.branches = branches
 if (state.selectedBranchId) {
 state.selectedBranch = branches.find((b: BranchOption) => b.id === state.selectedBranchId) ?? null
 if (!state.selectedBranch && branches.length > 0) {
 state.selectedBranchId = branches[0].id
 state.selectedBranch = branches[0]
 state.viewMode = 'branch'
 localStorage.setItem('selected_branch_id', branches[0].id)
 localStorage.setItem('branch_view_mode', 'branch')
 }
 }
 })
 .addCase(fetchBranches.rejected, (state) => {
 state.loading = false
 })
 },
})

export const { selectBranch, setCanSeeGlobalView, clearBranch } = branchSlice.actions
export default branchSlice.reducer
